import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DemCffSettingsPanel } from '../../components/dem/DemCffSettingsPanel';
import { DemDeliverySettingsPanel } from '../../components/dem/DemDeliverySettingsPanel';
import { DemSettingsIntegrationPanel } from '../../components/dem/DemSettingsIntegrationPanel';
import { PageHeader } from '../../components/ui/PageHeader';
import { useAdminHubContext } from '../../contexts/AdminHubContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalPath } from '../../contexts/PortalContext';
import { canManageDemConfigInAdminHub } from '../../lib/adminHubPermissions';
import { fetchEmailTemplates } from '../../lib/demOrderNotifications';
import type { DemEmailTemplates } from '../../lib/demOrderNotifications';
import {
  archiveOldDemOrders,
  fetchAllDemSequences,
  fetchDemIntegrationConfig,
  saveDemEmailTemplates,
  saveDemSequence,
} from '../../lib/demSettings';
import {
  canAccessDemSettings,
  canRevealDemIntegrationSecrets,
  canViewDemIntegrationSecrets,
} from '../../lib/demSettingsPermissions';
import type { DemIntegrationConfig, DemSequenceConfig, DemSequenceKey } from '../../types/dem';
import { DemMasterDataPage } from './DemMasterDataPage';

type DemSettingsSectionId =
  | 'master-data'
  | 'sequences'
  | 'integrations'
  | 'maintenance'
  | 'templates'
  | 'deliveries'
  | 'cff';

const SEQUENCE_ROWS: { key: DemSequenceKey; label: string; prefix: string }[] = [
  { key: 'po_sequence', label: 'PO (Purchase Order)', prefix: 'PO' },
  { key: 'b2b_sequence', label: 'B2B', prefix: 'B2B' },
  { key: 'wa_sequence', label: 'WhatsApp', prefix: 'WA' },
  { key: 'call_in_sequence', label: 'Manual Order', prefix: 'MI' },
];

function previewDemSequence(prefix: string, period: string, seq: string) {
  const p = period.trim() || 'YY-MM';
  const n = seq.trim() || '0';
  return `${prefix}${p}-${n}`;
}

const SECTIONS: { id: DemSettingsSectionId; label: string }[] = [
  { id: 'master-data', label: 'Master Data' },
  { id: 'sequences', label: 'Sequences' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'templates', label: 'Email templates' },
  { id: 'deliveries', label: 'Deliveries' },
  { id: 'cff', label: 'CFF' },
];

const DEFAULT_TEMPLATES: DemEmailTemplates = {
  pending_subject: 'Order Confirmation - {po_number}',
  pending_body: 'Dear Customer,\n\nWe have received your order {order_id}.\n\n{order_details_table}',
  processed_subject: 'Order Processed - {po_number}',
  processed_body: 'Dear Customer,\n\nYour order {order_id} is now being prepared.\n\n{order_details_table}',
};

function roleBadgeClass(role: string): string {
  if (role === 'it') return 'settings-role-badge settings-role-badge--it';
  if (role === 'admin') return 'settings-role-badge settings-role-badge--admin';
  if (role === 'supervisor') return 'settings-role-badge settings-role-badge--supervisor';
  return 'settings-role-badge settings-role-badge--user';
}

function sequenceDraftFrom(config: DemSequenceConfig | null) {
  return {
    period: config?.last_period ?? '',
    seq: config != null ? String(config.current_seq) : '',
  };
}

export function DemSettingsPage({
  embeddedInAdminHub = false,
  onlySection,
  omitSectionChrome = false,
}: {
  embeddedInAdminHub?: boolean;
  /** When set, only that DEM section is shown (used for Admin Hub Integrations tab). */
  onlySection?: DemSettingsSectionId;
  /** Render sequence cards only, for the unified Admin Hub Sequences panel. */
  omitSectionChrome?: boolean;
}) {
  const home = usePortalPath();
  const { profile, role, authUser } = useAuth();
  const hubCtx = useAdminHubContext();
  const inAdminHub = embeddedInAdminHub || hubCtx != null;
  const actorRole = hubCtx?.actorRole ?? role ?? 'user';
  const canAccess = inAdminHub
    ? canManageDemConfigInAdminHub(actorRole)
    : canAccessDemSettings(actorRole);

  const [section, setSection] = useState<DemSettingsSectionId>(
    onlySection ?? 'master-data',
  );
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [sequences, setSequences] = useState<Record<DemSequenceKey, DemSequenceConfig | null> | null>(null);
  const [sequenceDrafts, setSequenceDrafts] = useState<Record<DemSequenceKey, { period: string; seq: string }>>({
    po_sequence: { period: '', seq: '' },
    b2b_sequence: { period: '', seq: '' },
    wa_sequence: { period: '', seq: '' },
    call_in_sequence: { period: '', seq: '' },
  });
  const [sequencesLoading, setSequencesLoading] = useState(false);
  const [sequenceSaving, setSequenceSaving] = useState<DemSequenceKey | null>(null);

  const [templates, setTemplates] = useState<DemEmailTemplates>(DEFAULT_TEMPLATES);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesSaving, setTemplatesSaving] = useState(false);

  const [archiving, setArchiving] = useState(false);

  const [integrationConfig, setIntegrationConfig] = useState<DemIntegrationConfig | null>(null);
  const [integrationLoading, setIntegrationLoading] = useState(false);

  const showIntegration = inAdminHub
    ? canManageDemConfigInAdminHub(actorRole)
    : canViewDemIntegrationSecrets(actorRole);
  const canRevealSecrets = canRevealDemIntegrationSecrets(actorRole);
  const canEditSequences = canRevealDemIntegrationSecrets(actorRole);

  const navSections = SECTIONS.filter((item) => {
    if (onlySection) return item.id === onlySection;
    // Top-level Admin Hub tabs — hide matching sections inside DEM configuration.
    if (
      inAdminHub &&
      (item.id === 'integrations' ||
        item.id === 'sequences' ||
        item.id === 'templates' ||
        item.id === 'master-data' ||
        item.id === 'maintenance' ||
        item.id === 'deliveries' ||
        item.id === 'cff')
    ) {
      return false;
    }
    if (item.id === 'integrations' && !showIntegration) return false;
    return true;
  });

  const activeSection = onlySection ?? section;

  const loadSequences = useCallback(async () => {
    setSequencesLoading(true);
    setError(null);
    try {
      const data = await fetchAllDemSequences();
      setSequences(data);
      setSequenceDrafts({
        po_sequence: sequenceDraftFrom(data.po_sequence),
        b2b_sequence: sequenceDraftFrom(data.b2b_sequence),
        wa_sequence: sequenceDraftFrom(data.wa_sequence),
        call_in_sequence: sequenceDraftFrom(data.call_in_sequence),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sequences.');
    } finally {
      setSequencesLoading(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setError(null);
    try {
      const data = await fetchEmailTemplates();
      setTemplates(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load email templates.');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const loadIntegration = useCallback(async () => {
    if (!showIntegration) return;
    setIntegrationLoading(true);
    try {
      const data = await fetchDemIntegrationConfig();
      setIntegrationConfig(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load integration settings.');
      setIntegrationConfig(null);
    } finally {
      setIntegrationLoading(false);
    }
  }, [showIntegration]);

  useEffect(() => {
    if (!canAccess) return;
    if (!onlySection || onlySection === 'sequences') void loadSequences();
    if (!onlySection || onlySection === 'templates') void loadTemplates();
    if (!onlySection || onlySection === 'integrations') void loadIntegration();
  }, [canAccess, onlySection, loadSequences, loadTemplates, loadIntegration]);

  if (!canAccess) {
    if (inAdminHub) {
      return (
        <p className={`settings-empty${omitSectionChrome ? ' sequences-admin__status' : ''}`}>
          You cannot manage DEM sequences.
        </p>
      );
    }
    return <Navigate to={home} replace />;
  }

  const displayName = profile?.display_name?.trim() || profile?.username || 'User';
  const email = authUser?.email?.trim() || profile?.email?.trim() || '';
  const emailDisplay = email || '—';

  const handleSaveSequence = async (key: DemSequenceKey) => {
    if (!canEditSequences) {
      setError('Only IT can update sequence values.');
      return;
    }
    const draft = sequenceDrafts[key];
    const rawSeq = draft.seq.trim();
    if (!rawSeq || !/^\d+$/.test(rawSeq)) {
      setError('Sequence must be a whole number.');
      return;
    }

    setSequenceSaving(key);
    setError(null);
    setStatusMessage(null);
    try {
      await saveDemSequence(key, draft.period, Number(rawSeq));
      setStatusMessage(`${SEQUENCE_ROWS.find((row) => row.key === key)?.label ?? 'Sequence'} updated.`);
      await loadSequences();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update sequence.');
    } finally {
      setSequenceSaving(null);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Mark Processed orders older than 60 days as Archived?\n\nCancelled/voided orders are left unchanged.\n\nContinue?')) {
      return;
    }

    setArchiving(true);
    setError(null);
    setStatusMessage(null);
    try {
      await archiveOldDemOrders();
      setStatusMessage('Old orders archived successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive orders.');
    } finally {
      setArchiving(false);
    }
  };

  const handleSaveTemplates = async () => {
    if (!canEditSequences) {
      setError('Only IT can update email templates.');
      return;
    }
    setTemplatesSaving(true);
    setError(null);
    setStatusMessage(null);
    try {
      await saveDemEmailTemplates(templates);
      setStatusMessage('Email templates saved to cloud.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save email templates.');
    } finally {
      setTemplatesSaving(false);
    }
  };

  const sequenceRows = SEQUENCE_ROWS.map((row) => {
    const draft = sequenceDrafts[row.key];
    const current = sequences?.[row.key] ?? null;
    const prefix = current?.prefix?.trim() || row.prefix;
    const period = draft.period.trim() || current?.last_period || '';
    const seq = draft.seq.trim() || (current != null ? String(current.current_seq) : '');
    const preview = previewDemSequence(prefix, period, seq);
    return (
      <div key={row.key} className="ticket-settings-row sequences-admin__row">
        <div className="sequences-admin__row-meta">
          <p className="ticket-settings-row__title">{row.label}</p>
          <p className="ticket-settings-row__hint">{preview}</p>
        </div>
        <div className="sequences-admin__row-fields">
          <label className="dem-settings-field">
            <span>Period</span>
            <input
              className="dem-settings-input dem-settings-input--short"
              value={draft.period}
              placeholder={current?.last_period || 'N/A'}
              disabled={!canEditSequences}
              onChange={(e) =>
                setSequenceDrafts((prev) => ({
                  ...prev,
                  [row.key]: { ...prev[row.key], period: e.target.value },
                }))
              }
            />
          </label>
          <label className="dem-settings-field">
            <span>Seq No</span>
            <input
              className="dem-settings-input dem-settings-input--short"
              value={draft.seq}
              placeholder={current != null ? String(current.current_seq) : '0'}
              disabled={!canEditSequences}
              onChange={(e) =>
                setSequenceDrafts((prev) => ({
                  ...prev,
                  [row.key]: { ...prev[row.key], seq: e.target.value },
                }))
              }
            />
          </label>
          {canEditSequences ? (
            <button
              type="button"
              className="btn-primary btn-sm sequences-admin__row-update"
              disabled={sequenceSaving === row.key}
              onClick={() => void handleSaveSequence(row.key)}
            >
              {sequenceSaving === row.key ? 'Saving…' : 'Update'}
            </button>
          ) : null}
        </div>
      </div>
    );
  });

  const demTemplateEditors = (
    <div className="email-templates-dem">
      {error ? <p className="alert alert-error">{error}</p> : null}
      {statusMessage ? (
        <p className="dem-uid-status dem-uid-status--ok" role="status">
          {statusMessage}
        </p>
      ) : null}
      {templatesLoading ? <p className="settings-empty">Loading email templates…</p> : null}
      <>
          <section className="ticket-mailbox-setup">
            <div className="settings-panel__head">
              <div>
                <h2 className="settings-panel__title">Pending order</h2>
              </div>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => void loadTemplates()}
                disabled={templatesLoading}
              >
                Reload
              </button>
            </div>
            <label className="dem-settings-field dem-settings-field--block">
              <span>Subject line</span>
              <input
                className="dem-settings-input"
                value={templates.pending_subject}
                disabled={!canEditSequences}
                onChange={(e) => setTemplates((prev) => ({ ...prev, pending_subject: e.target.value }))}
              />
            </label>
            <label className="dem-settings-field dem-settings-field--block">
              <span>Email body</span>
              <textarea
                className="dem-settings-textarea"
                rows={6}
                value={templates.pending_body}
                disabled={!canEditSequences}
                onChange={(e) => setTemplates((prev) => ({ ...prev, pending_body: e.target.value }))}
              />
            </label>
          </section>

          <section className="ticket-mailbox-setup">
            <h2 className="settings-panel__title">Processed order</h2>
            <label className="dem-settings-field dem-settings-field--block">
              <span>Subject line</span>
              <input
                className="dem-settings-input"
                value={templates.processed_subject}
                disabled={!canEditSequences}
                onChange={(e) =>
                  setTemplates((prev) => ({ ...prev, processed_subject: e.target.value }))
                }
              />
            </label>
            <label className="dem-settings-field dem-settings-field--block">
              <span>Email body</span>
              <textarea
                className="dem-settings-textarea"
                rows={6}
                value={templates.processed_body}
                disabled={!canEditSequences}
                onChange={(e) => setTemplates((prev) => ({ ...prev, processed_body: e.target.value }))}
              />
            </label>
            {canEditSequences ? (
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={templatesSaving}
                onClick={() => void handleSaveTemplates()}
                style={{ marginTop: '0.75rem' }}
              >
                {templatesSaving ? 'Saving…' : 'Save email templates'}
              </button>
            ) : null}
          </section>
      </>
    </div>
  );

  if (omitSectionChrome) {
    if (onlySection === 'templates') {
      return demTemplateEditors;
    }
    return (
      <>
        {error ? (
          <p className="alert alert-error sequences-admin__status">{error}</p>
        ) : null}
        {statusMessage ? (
          <p className="dem-uid-status dem-uid-status--ok sequences-admin__status" role="status">
            {statusMessage}
          </p>
        ) : null}
        {sequencesLoading && !sequences ? (
          <p className="settings-empty sequences-admin__status">Loading sequences…</p>
        ) : (
          sequenceRows
        )}
      </>
    );
  }

  return (
    <div className={`settings-page dem-settings-page${inAdminHub ? ' dem-settings-page--embedded' : ''}`}>
      {!inAdminHub && (
        <>
          <PageHeader title="Settings" />

          <section className="card settings-profile-card">
            <div className="settings-profile-card__avatar" aria-hidden>
              {displayName[0]?.toUpperCase() ?? '?'}
            </div>
            <div className="settings-profile-card__info">
              <p className="settings-profile-card__eyebrow">Your session</p>
              <h2 className="settings-profile-card__name">{displayName}</h2>
            </div>
            <p className="settings-profile-card__email">{emailDisplay}</p>
            <div className="settings-profile-card__meta">
              <span className={roleBadgeClass(actorRole)}>{actorRole}</span>
              {profile?.username && (
                <span className="settings-profile-card__username">@{profile.username}</span>
              )}
            </div>
          </section>
        </>
      )}

      {error && <p className="alert alert-error">{error}</p>}
      {statusMessage && (
        <p className="dem-uid-status dem-uid-status--ok" role="status">
          {statusMessage}
        </p>
      )}

      {navSections.length > 1 ? (
        <nav className="af-settings-nav dem-settings-nav" aria-label="DEM configuration sections">
          {navSections.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`af-settings-nav__tab${activeSection === item.id ? ' af-settings-nav__tab--active' : ''}`}
              aria-current={activeSection === item.id ? 'page' : undefined}
              onClick={() => setSection(item.id)}
            >
              <span className="af-settings-nav__label">{item.label}</span>
            </button>
          ))}
        </nav>
      ) : null}

      {activeSection === 'master-data' ? (
        <section className="card settings-panel">
          <DemMasterDataPage embedded />
        </section>
      ) : null}

      {activeSection === 'sequences' ? (
        <section className="card settings-panel">
          <div className="settings-panel__head">
            <div>
              <h2 className="settings-panel__title">Sequences</h2>
            </div>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => void loadSequences()}
              disabled={sequencesLoading}
            >
              Refresh
            </button>
          </div>

          <div className="ticket-settings-rows sequences-admin__list">{sequenceRows}</div>
        </section>
      ) : null}

      {activeSection === 'integrations' && showIntegration ? (
        <>
          {integrationLoading && !integrationConfig ? (
            <p className="page-lead">Loading…</p>
          ) : integrationConfig ? (
            <DemSettingsIntegrationPanel
              config={integrationConfig}
              showFullIntegration
              canRevealSecrets={canRevealSecrets}
              actorEmail={email}
              refreshing={integrationLoading}
              onRefresh={() => void loadIntegration()}
            />
          ) : null}
        </>
      ) : null}

      {activeSection === 'maintenance' ? (
        <section className="card settings-panel settings-panel--secondary">
          <div className="settings-panel__head">
            <div>
              <h2 className="settings-panel__title">Maintenance</h2>
            </div>
          </div>
          <button
            type="button"
            className="btn-danger-outline btn-sm"
            disabled={archiving || !canEditSequences}
            onClick={() => void handleArchive()}
          >
            {archiving ? 'Archiving…' : 'Archive old orders'}
          </button>
        </section>
      ) : null}

      {activeSection === 'templates' ? (
        <div className="dem-settings-panel dem-settings-panel--stacked">{demTemplateEditors}</div>
      ) : null}

      {activeSection === 'deliveries' ? <DemDeliverySettingsPanel /> : null}
      {activeSection === 'cff' ? <DemCffSettingsPanel canEdit={canAccess} /> : null}
    </div>
  );
}
